// Geometry for editing parking slots on the map. Pure functions — no Google
// Maps, no React — so the maths can be tested on its own and the map code is
// only glue.
//
// Everything is done in a local metre plane (equirectangular about a chosen
// origin), the same projection backend/app/services/parking_geometry.py uses.
// That matters twice over: a degree of longitude is shorter than a degree of
// latitude, so rotating or measuring in raw degrees would shear a rectangle;
// and the server re-applies move/rotate itself, so the preview here has to
// land on exactly the same coordinates.
//
// Conventions: points are [lat, lng]. The plane has x = east, y = north.
// Angles are radians (or degrees where named so) measured counter-clockwise
// from east, i.e. on a north-up map positive rotation turns anticlockwise.

const R = 6_371_000;
const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

export function planeAt(lat0, lng0) {
  const k = Math.cos(toRad(lat0));
  return {
    toXY: (lat, lng) => [toRad(lng - lng0) * R * k, toRad(lat - lat0) * R],
    toLatLng: (x, y) => [lat0 + toDeg(y / R), lng0 + toDeg(x / (R * k))],
  };
}

export function centroid(points) {
  const n = points.length;
  return [points.reduce((s, p) => s + p[0], 0) / n, points.reduce((s, p) => s + p[1], 0) / n];
}

// ------------------------------------------------------------------ rectangles
//
// A rectangle is { lat, lng, w, h, theta }: its centre, its size in metres and
// the direction of its width axis ("u"). The other axis ("v") points to the
// right of u, so the corners in (u, v) order are top-left, top-right,
// bottom-right, bottom-left — the same order the backend generates slots in.

const axes = (theta) => ({
  u: [Math.cos(theta), Math.sin(theta)],
  v: [Math.sin(theta), -Math.cos(theta)],
});

// Fit a rectangle to a slot outline. A generated slot is a near-rectangle (the
// grid is interpolated between four drawn corners, so it can be very slightly
// skewed); this snaps it to the nearest true rectangle, which is what editing
// then keeps it as.
export function ringToRect(ring) {
  const [lat, lng] = centroid(ring);
  const plane = planeAt(lat, lng);
  const xy = ring.map((p) => plane.toXY(p[0], p[1]));
  if (xy.length >= 4) {
    const [p0, p1, p2, p3] = xy;
    const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
    return {
      lat, lng,
      w: (dist(p0, p1) + dist(p3, p2)) / 2,
      h: (dist(p1, p2) + dist(p0, p3)) / 2,
      theta: Math.atan2(p1[1] - p0[1], p1[0] - p0[0]),
    };
  }
  // Fewer than four points: fall back to the axis-aligned box around them.
  const xs = xy.map((p) => p[0]);
  const ys = xy.map((p) => p[1]);
  return { lat, lng, w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys), theta: 0 };
}

export function rectToRing(rect) {
  const plane = planeAt(rect.lat, rect.lng);
  const { u, v } = axes(rect.theta);
  return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([a, b]) => {
    const du = (a * rect.w) / 2;
    const dv = (b * rect.h) / 2;
    return plane.toLatLng(u[0] * du + v[0] * dv, u[1] * du + v[1] * dv);
  });
}

// The eight resize handles: four corners and four edge midpoints. (sx, sy) is
// which side each one drags: -1 / +1 for the min / max side of that axis, 0 for
// "this axis is not resized".
export const HANDLES = [
  [-1, -1], [0, -1], [1, -1],
  [-1, 0], [1, 0],
  [-1, 1], [0, 1], [1, 1],
];

export function handlePoints(rect) {
  const plane = planeAt(rect.lat, rect.lng);
  const { u, v } = axes(rect.theta);
  return HANDLES.map(([sx, sy]) => {
    const du = (sx * rect.w) / 2;
    const dv = (sy * rect.h) / 2;
    const [lat, lng] = plane.toLatLng(u[0] * du + v[0] * dv, u[1] * du + v[1] * dv);
    return { sx, sy, lat, lng };
  });
}

export const MIN_SIDE_M = 0.5;

// Drag handle (sx, sy) to `pointer`. The opposite edge/corner stays exactly
// where it was, so resizing behaves like resizing a rectangle in a drawing app:
// only the side being pulled moves, and only along the rectangle's own axes.
export function resizeRect(rect, sx, sy, pointer, minSide = MIN_SIDE_M) {
  const plane = planeAt(rect.lat, rect.lng);
  const { u, v } = axes(rect.theta);
  const [px, py] = plane.toXY(pointer[0], pointer[1]);
  const pu = px * u[0] + py * u[1];
  const pv = px * v[0] + py * v[1];

  let left = -rect.w / 2;
  let right = rect.w / 2;
  let top = -rect.h / 2;
  let bottom = rect.h / 2;
  if (sx === 1) right = Math.max(pu, left + minSide);
  if (sx === -1) left = Math.min(pu, right - minSide);
  if (sy === 1) bottom = Math.max(pv, top + minSide);
  if (sy === -1) top = Math.min(pv, bottom - minSide);

  const cu = (left + right) / 2;
  const cv = (top + bottom) / 2;
  const [lat, lng] = plane.toLatLng(u[0] * cu + v[0] * cv, u[1] * cu + v[1] * cv);
  return { lat, lng, w: right - left, h: bottom - top, theta: rect.theta };
}

export function translateRect(rect, dLat, dLng) {
  return { ...rect, lat: rect.lat + dLat, lng: rect.lng + dLng };
}

// A copy of the rectangle placed alongside it (one width over, plus a small
// gap), so a duplicate is visibly a separate slot rather than sitting exactly
// on top of its original.
export function neighbourRect(rect, gapM = 0.3) {
  const plane = planeAt(rect.lat, rect.lng);
  const { u } = axes(rect.theta);
  const dist = rect.w + gapM;
  const [lat, lng] = plane.toLatLng(u[0] * dist, u[1] * dist);
  return { ...rect, lat, lng };
}

// Angle (radians, anticlockwise from east) of `pointer` as seen from `centre`,
// and how far it is, in metres. The rotate knob uses the angle *difference*
// between where a drag started and where it is now.
export function bearingFrom(centre, pointer) {
  const [x, y] = planeAt(centre[0], centre[1]).toXY(pointer[0], pointer[1]);
  return { angle: Math.atan2(y, x), distanceM: Math.hypot(x, y) };
}

// ------------------------------------------------------------- whole-area moves

// Rotate `ring` about `pivot` by `deg` (anticlockwise), then shift it east /
// north in metres. Mirrors transform_ring in the backend.
export function transformRing(ring, pivot, deg, eastM = 0, northM = 0) {
  const plane = planeAt(pivot[0], pivot[1]);
  const t = toRad(deg);
  const cos = Math.cos(t);
  const sin = Math.sin(t);
  return ring.map((p) => {
    const [x, y] = plane.toXY(p[0], p[1]);
    return plane.toLatLng(x * cos - y * sin + eastM, x * sin + y * cos + northM);
  });
}

// A latitude/longitude difference near `lat`, as metres east / north.
export function metersFromDelta(lat, dLat, dLng) {
  return { east: toRad(dLng) * R * Math.cos(toRad(lat)), north: toRad(dLat) * R };
}

export const radToDeg = toDeg;
